import React, { useEffect, useMemo, useState } from 'react';
import { fetchWaTemplates } from '../../../lib/api';
import TemplateCard from '../components/TemplateCard';
import '../styles/templates.css';

const PAGE_SIZE = 10;

const SkeletonCard = () => (
  <div className="template-card skeleton">
    <div className="skeleton-block" />
    <div className="skeleton-block short" />
    <div className="skeleton-block" />
  </div>
);

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadTemplates(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTemplates = async (page = 1, reset = false) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError('');

    try {
      const response = await fetchWaTemplates({ page, limit: PAGE_SIZE });
      const incoming = Array.isArray(response?.templates) ? response.templates : [];
      const base = reset ? [] : templates;
      const deduped = incoming.filter((tpl) => !base.some((existing) => existing.id === tpl.id));
      const merged = reset ? deduped : [...base, ...deduped];

      setTemplates(merged);
      setCategories([
        ...new Set(
          merged
            .map((tpl) => tpl.wa_template_category)
            .filter((category) => typeof category === 'string' && category.trim().length > 0)
        ),
      ]);
      const hasMoreFlag = typeof response?.pagination?.has_next === 'boolean'
        ? response.pagination.has_next
        : Array.isArray(response?.templates) && response.templates.length >= PAGE_SIZE;
      setHasMore(hasMoreFlag);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to fetch templates', err);
      setError(err?.message || 'Failed to fetch templates.');
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const filteredTemplates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return templates.filter((template) => {
      const matchesSearch = term
        ? (template?.name || '')
            .toString()
            .toLowerCase()
            .includes(term) ||
          (template?.code || '').toLowerCase().includes(term)
        : true;
      const matchesCategory = selectedCategory
        ? template?.wa_template_category === selectedCategory
        : true;
      return matchesSearch && matchesCategory;
    });
  }, [templates, searchTerm, selectedCategory]);

  return (
    <div className="templates-page">
      <header className="templates-page__header">
        <h2>All Templates</h2>
        <p>Browse message templates available to your account.</p>
      </header>

      <div className="templates-page__filters">
        <input
          type="search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search templates"
          aria-label="Search templates"
        />
        <select
          value={selectedCategory}
          onChange={(event) => setSelectedCategory(event.target.value)}
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="templates-page__error">{error}</div>}

      <section className="templates-grid" aria-live="polite">
        {loading && templates.length === 0 ? (
          Array.from({ length: 6 }).map((_, index) => <SkeletonCard key={index} />)
        ) : filteredTemplates.length > 0 ? (
          filteredTemplates.map((template) => <TemplateCard key={template.id || template.name} template={template} />)
        ) : (
          <p className="templates-page__empty">No templates found.</p>
        )}
      </section>

      {hasMore && !loading && (
        <div className="templates-page__load-more">
          <button type="button" className="btn" onClick={() => loadTemplates(currentPage + 1)} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default Templates;
